/**
 * Catalogue centralisé des events PostHog (source de vérité unique).
 *
 * Règle : on n'ajoute un event custom QUE lorsqu'il porte une donnée métier que
 * l'autocapture PostHog ne peut pas deviner (intent, MBTI, lead, étape de funnel).
 * Les clics/pages/formulaires génériques sont déjà couverts par l'autocapture.
 *
 * Le nommage est `snake_case`, stable, et figé : Max AI / le MCP PostHog s'appuient
 * dessus pour répondre aux questions. Ne pas renommer un event sans migration.
 */
export const TRACKING_EVENTS = {
  // ── Zone 1 — Acquisition SEO / contenu ──────────────────────────────
  /** Clic sur le CTA Lola d'une page de contenu (pont SEO → funnel). */
  CTA_LOLA_CLICKED: 'cta_lola_clicked',
  /** Recherche effectuée (écoles / métiers / articles). */
  SEARCH_PERFORMED: 'search_performed',
  /** Recherche établissement aboutie (filtres + géo + résultats API). */
  ESTABLISHMENT_SEARCH_EXECUTED: 'establishment_search_executed',
  /** Clic « Consulter » sur un résultat école ou formation. */
  ESTABLISHMENT_RESULT_CONSULTED: 'establishment_result_consulted',
  /** Activation / désactivation de « rechercher sur la carte ». */
  ESTABLISHMENT_SEARCH_MAP_MODE_TOGGLED: 'establishment_search_map_mode_toggled',
  /** Changement de langue via le sélecteur navbar / menu mobile. */
  LOCALE_SWITCHED: 'locale_switched',
  /** Clic sur un marqueur de la carte (engagement exploration carte → quelle entité). */
  ESTABLISHMENT_MAP_MARKER_CLICKED: 'establishment_map_marker_clicked',

  /** Choix du bandeau cookies analytics (accepté / refusé). */
  COOKIE_CONSENT: 'cookie_consent',

  // ── Zone 2 — Conversion (funnel) ────────────────────────────────────
  /** Entrée dans le tunnel SEA d'une landing page. */
  TUNNEL_STARTED: 'tunnel_started',
  /** Affichage d'une étape du tunnel SEA (suivi des abandons, étape par étape). */
  TUNNEL_STEP_VIEWED: 'tunnel_step_viewed',
  /** Étape du tunnel validée. */
  TUNNEL_STEP_COMPLETED: 'tunnel_step_completed',
  /** Étape du tunnel passée ("je ne sais pas" / "passer"). */
  TUNNEL_STEP_SKIPPED: 'tunnel_step_skipped',
  /** Formulaire d'inscription du tunnel soumis. */
  REGISTER_SUBMITTED: 'register_submitted',
  /** Échec de la création du lead à l'inscription du tunnel (raison déduite de la réponse API). */
  REGISTER_FAILED: 'register_failed',
  /** Lead créé (utilisateur temporaire provisionné). Conversion clé. */
  LEAD_CREATED: 'lead_created',
  /** Téléphone renseigné et enregistré à l'étape 5 du tunnel SEA. */
  TUNNEL_PHONE_SUBMITTED: 'tunnel_phone_submitted',
  /** Étape téléphone du tunnel SEA passée sans renseigner de numéro. */
  TUNNEL_PHONE_SKIPPED: 'tunnel_phone_skipped',
  /** Échec de l'enregistrement de l'accord pour être appelé par les écoles (étape téléphone du tunnel ou de l'onboarding). */
  SCHOOL_PHONE_CONTACT_CONSENT_SAVE_FAILED: 'school_phone_contact_consent_save_failed',
  /** Entrée dans l'onboarding d'un compte connecté qui n'a pas encore répondu aux questions du tunnel. */
  ONBOARDING_STARTED: 'onboarding_started',
  /** Affichage d'une étape de l'onboarding (suivi des abandons, étape par étape). */
  ONBOARDING_STEP_VIEWED: 'onboarding_step_viewed',
  /** Étape de l'onboarding validée. */
  ONBOARDING_STEP_COMPLETED: 'onboarding_step_completed',
  /** Étape de l'onboarding passée (« je ne sais pas »). */
  ONBOARDING_STEP_SKIPPED: 'onboarding_step_skipped',
  /** Échec d'un enregistrement de l'onboarding (réponses ou téléphone). */
  ONBOARDING_SAVE_FAILED: 'onboarding_save_failed',
  /** Téléphone renseigné à la dernière étape de l'onboarding. */
  ONBOARDING_PHONE_SUBMITTED: 'onboarding_phone_submitted',
  /** Dernière étape de l'onboarding passée sans renseigner de numéro. */
  ONBOARDING_PHONE_SKIPPED: 'onboarding_phone_skipped',
  /** Inscription/connexion démarrée (Google / Facebook / email). */
  SIGNUP_STARTED: 'signup_started',
  /** Compte créé avec succès (register API, retour OAuth signup ou lead SEA). */
  SIGNUP_COMPLETED: 'signup_completed',

  // ── Zone 3 — Produit & rétention (le "trou" du funnel) ──────────────
  /** Ouverture de la conversation Lola. */
  LOLA_CONVERSATION_OPENED: 'lola_conversation_opened',
  /** Sélection d'une conversation dans le menu (Lola ou école). */
  CONVERSATION_SELECTED: 'conversation_selected',
  /** Ouverture du menu multi-conversations (drawer mobile). */
  CONVERSATIONS_MENU_OPENED: 'conversations_menu_opened',
  /** Ouverture de la modale « nouvelle discussion ». */
  CONVERSATIONS_START_MODAL_OPENED: 'conversations_start_modal_opened',
  /** Fermeture de la modale « nouvelle discussion ». */
  CONVERSATIONS_START_MODAL_CLOSED: 'conversations_start_modal_closed',
  /** Recherche dans la modale « nouvelle discussion ». */
  CONVERSATIONS_START_MODAL_SEARCHED: 'conversations_start_modal_searched',
  /** Sélection d'une école dans la modale « nouvelle discussion ». */
  CONVERSATIONS_START_MODAL_SCHOOL_SELECTED: 'conversations_start_modal_school_selected',
  /** Démarrage ou reprise d'une discussion avec une école. */
  CONVERSATION_STARTED: 'conversation_started',
  /** Message envoyé à une école (composer du fil école). */
  SCHOOL_MESSAGE_SENT: 'school_message_sent',
  /** Clic sur une action du panneau conseiller école (rappel, rendez-vous…). */
  SCHOOL_ADVISOR_ACTION_SELECTED: 'school_advisor_action_selected',
  /** Message envoyé à Lola (input / suggestion / carte programme). */
  LOLA_MESSAGE_SENT: 'lola_message_sent',
  /** Ouverture du drawer « Lola cherche pour toi » (critères de recherche). */
  LOLA_SEARCH_FILTERS_OPENED: 'lola_search_filters_opened',
  /** Ajout d'un critère via un tag du drawer filtres (Lola voudrait encore savoir, ou intérêt suggéré d'un domaine). */
  LOLA_SEARCH_FILTER_CRITERION_ADDED: 'lola_search_filter_criterion_added',
  /** Retrait d'un critère déduit (croix chips ou tag déjà coché). */
  LOLA_SEARCH_FILTER_CRITERION_REMOVED: 'lola_search_filter_criterion_removed',
  /** Retrait d'un domaine d'intérêts et de tous ses intérêts (poubelle de sa carte, drawer filtres). */
  LOLA_SEARCH_FILTER_INTEREST_DOMAIN_REMOVED: 'lola_search_filter_interest_domain_removed',
  /** Ouverture de l'écran « Tes documents » (PJ Lola). */
  LOLA_ATTACHMENTS_OPENED: 'lola_attachments_opened',
  /** Téléchargement d'une pièce jointe Lola. */
  LOLA_ATTACHMENT_DOWNLOADED: 'lola_attachment_downloaded',
  /** Changement de catégorie documentaire d'une PJ Lola. */
  LOLA_ATTACHMENT_CATEGORY_CHANGED: 'lola_attachment_category_changed',
  /** Réponse de Lola reçue (porte intent / confidence / état conversation). */
  LOLA_MESSAGE_RECEIVED: 'lola_message_received',
  /** Recommandations affichées (moment de valeur livrée). */
  RECOMMENDATIONS_SHOWN: 'recommendations_shown',
  /** Ouverture du menu résultats Lola (drawer mobile « Voir les résultats »). */
  LOLA_RESULTS_MENU_OPENED: 'lola_results_menu_opened',
  /** Onglet du panneau détail consulté (Moi / Écoles / Métiers / Favoris). */
  DETAIL_TAB_VIEWED: 'detail_tab_viewed',
  /** Clic « Pose-moi une question » sur une dimension (onglet Résumé) : question envoyée à Lola. */
  DIMENSION_QUESTION_REQUESTED: 'dimension_question_requested',
  /** Réponse au portrait « Ce que j'ai compris de toi » (onglet Profil) : « Oui, c'est moi » ou « Pas tout à fait », envoyée à Lola. */
  PROFILE_SUMMARY_ANSWERED: 'profile_summary_answered',
  /** Erreur pendant un échange avec Lola (surveillance santé). */
  LOLA_ERROR: 'lola_error',
  /** Progression dans le test MBTI (question par question). */
  MBTI_TEST_PROGRESSED: 'mbti_test_progressed',
  /** Résultat MBTI affiché (point de décrochage produit à surveiller). */
  MBTI_RESULT_VIEWED: 'mbti_result_viewed',
  /** Ouverture du tiroir « Ton profil » (réception du profil puis autorisations), avec le bouton d'origine. */
  PROFILE_REPORT_FLOW_OPENED: 'profile_report_flow_opened',
  /** Affichage d'une étape du tiroir « Ton profil » (suivi des abandons, étape par étape). */
  PROFILE_REPORT_FLOW_STEP_VIEWED: 'profile_report_flow_step_viewed',
  /** Rapport de personnalité envoyé sur WhatsApp ou par email (succès ou échec de l'envoi). */
  PERSONALITY_REPORT_SENT: 'personality_report_sent',
  /** Clic « Continuer sans recevoir mon profil » dans le tiroir « Ton profil ». */
  PERSONALITY_REPORT_SKIPPED: 'personality_report_skipped',
  /** Vidéo de présentation ouverte (page Notre mission). */
  PRESENTATION_VIDEO_OPENED: 'presentation_video_opened',
  /** Choix de l'étape « Qui peut voir quoi ? » enregistrés (mise en relation et autorisations facultatives). */
  PARTNER_CONTACT_CONSENT_SAVED: 'partner_contact_consent_saved',
  /** Échec d'enregistrement d'une autorisation facultative (personnalité, documents, entreprises, établissements retirés, WhatsApp). */
  CONSENT_SAVE_FAILED: 'consent_save_failed',
  /** Consultation de la fiche d'un programme recommandé. */
  PROGRAM_CONSULTED: 'program_consulted',
  /** Carte programme dépliée (accordéon, sans clic « détail »). */
  PROGRAM_EXPANDED: 'program_expanded',
  /** Question posée sur un programme recommandé. */
  PROGRAM_QUESTION_ASKED: 'program_question_asked',
  /** Demande de contact d'un programme (intention forte pré-lead). */
  PROGRAM_CONTACTED: 'program_contacted',
  /** Programme retiré des recommandations (clic poubelle de la carte école). */
  PROGRAM_REJECTED: 'program_rejected',
  /** Comparaison de plusieurs programmes. */
  PROGRAMS_COMPARED: 'programs_compared',
  /** Vérification des chances d'admission. */
  ADMISSION_CHECKED: 'admission_checked',
  /** Ajout/retrait d'un favori (programme ou métier). */
  FAVORITE_TOGGLED: 'favorite_toggled',
  /** Changement de thème couleur sur `/discuter` (toggle utilisateur). */
  CHAT_THEME_CHANGED: 'chat_theme_changed',
  /** Thème chat résolu à l'entrée sur `/discuter` (cookie ou système). */
  CHAT_THEME_RESOLVED: 'chat_theme_resolved',

  // ── Zone 4 — Profil public partagé (boucle virale) ──────────────────
  /** Ouverture du panneau « Partager ton profil » (drawer chat). */
  PROFILE_SHARE_PANEL_OPENED: 'profile_share_panel_opened',
  /** Partage du profil via un canal (WhatsApp / SMS / mail / copie) ou ouverture de l'aperçu complet. */
  PROFILE_SHARED: 'profile_shared',
  /** Bulle « Ton profil est partageable » affichée (une fois par appareil, quand le profil devient partageable). */
  PROFILE_SHARE_BUBBLE_SHOWN: 'profile_share_bubble_shown',
  /** Clic dans la bulle de partage : un réseau (`channel`), ou la carte et « Partager mon profil » (`channel` à `null`). */
  PROFILE_SHARE_BUBBLE_CLICKED: 'profile_share_bubble_clicked',
  /** Bulle de partage fermée sans partager (croix ou appui en dehors). */
  PROFILE_SHARE_BUBBLE_DISMISSED: 'profile_share_bubble_dismissed',
  /** Affichage d'un profil public (lien partagé reçu ou page perso). */
  PUBLIC_PROFILE_VIEWED: 'public_profile_viewed',
  /** Clic sur un CTA « fais ton profil » du profil public (pont partage → inscription). */
  PUBLIC_PROFILE_CTA_CLICKED: 'public_profile_cta_clicked',
  /** Recommandations montrées ou cachées aux personnes à qui l'utilisateur partage son profil (choix enregistré). */
  PUBLIC_PROFILE_RECOMMENDATIONS_SHARING_SAVED: 'public_profile_recommendations_sharing_saved',

  // ── Zone 5 — Compte : vérification email / téléphone ────────────────
  /** Code de vérification envoyé par email ou SMS depuis « Mon compte ». */
  ACCOUNT_VERIFICATION_CODE_SENT: 'account_verification_code_sent',
  /** Email ou téléphone validé avec le code reçu. */
  ACCOUNT_VERIFICATION_CONFIRMED: 'account_verification_confirmed',
  /** Échec de l'envoi du code ou code refusé. */
  ACCOUNT_VERIFICATION_FAILED: 'account_verification_failed',
} as const

/** Nom d'un event de tracking (valeur du catalogue `TRACKING_EVENTS`). */
export type TrackingEventName = (typeof TRACKING_EVENTS)[keyof typeof TRACKING_EVENTS]
